import { watchHrefAllowed, type WatchScript } from '@secretario/shared';
import { useSyncExternalStore } from 'react';

/**
 * O modo Assistir (F1 do "Secretário faz tudo"): com ele ligado, depois que o Secretário faz
 * uma ação, a tela repete o que aconteceu (abre a página, preenche a folha, aperta o botão e
 * acende o item novo). Começa desligado; a escolha fica neste aparelho. A repetição guarda o
 * começo aqui fora: cada tela que abre continua do ponto certo, pelo relógio.
 */
const KEY = 'secretario.assistir';
const listeners = new Set<() => void>();
const emit = () => {
  for (const listener of listeners) listener();
};
const subscribe = (listener: () => void) => {
  listeners.add(listener);
  return () => listeners.delete(listener);
};

let pref: boolean | null = null;
function readPref(): boolean {
  if (pref !== null) return pref;
  try {
    pref = window.localStorage.getItem(KEY) === '1';
  } catch {
    pref = false;
  }
  return pref;
}

export function setWatchPref(on: boolean): void {
  pref = on;
  try {
    window.localStorage.setItem(KEY, on ? '1' : '0');
  } catch {
    /* sem armazenamento: vale até fechar o app */
  }
  emit();
}

export function useWatchPref(): boolean {
  return useSyncExternalStore(subscribe, readPref, () => false);
}

export type WatchRun = { script: WatchScript; startedAt: number; id: number };
let current: WatchRun | null = null;
let next = 1;

/** Começa a repetição (só telas do trabalho; o resto o app não abre sozinho). */
export function playWatch(script: WatchScript): boolean {
  if (!watchHrefAllowed(script.href)) return false;
  current = { script, startedAt: Date.now(), id: next++ };
  emit();
  return true;
}

export function stopWatch(): void {
  current = null;
  emit();
}

export function useWatchRun(): WatchRun | null {
  return useSyncExternalStore(
    subscribe,
    () => current,
    () => null,
  );
}

/** O tempo de cada parte da repetição (ms). */
export const WATCH_TIMING = {
  open: 350,
  perChar: 32,
  gap: 220,
  press: 520,
  close: 380,
  highlight: 2600,
};

/** Em que ponto está a repetição neste instante. */
export function watchPhase(run: WatchRun, now: number) {
  const t = now - run.startedAt;
  const { open, perChar, gap, press, close, highlight } = WATCH_TIMING;
  let cursor = open;
  const typed = run.script.fields.map((row) => {
    const start = cursor;
    const span = Math.max(1, row.value.length) * perChar;
    cursor += span + gap;
    const chars = t <= start ? 0 : Math.min(row.value.length, Math.floor((t - start) / perChar));
    return { ...row, shown: row.value.slice(0, chars), typing: t > start && t < start + span };
  });
  const pressAt = cursor;
  const closeAt = pressAt + press;
  const hitAt = closeAt + close;
  const endAt = hitAt + highlight;
  return {
    t,
    typed,
    pressing: t >= pressAt && t < closeAt,
    sheet: t < closeAt + close,
    closing: t >= closeAt,
    highlighting: t >= hitAt && t < endAt,
    done: t >= endAt,
  };
}
